// @ts-nocheck
// Shared Utilities for Calculators
// Eliminates copy-paste code duplication across components

// ===== FORMATTING UTILITIES =====

export const formatters = {
  // Currency formatting
  currency: (value: number, currency: string = 'CAD'): string => {
    const symbol = currency === 'USD' ? '$' : '$';
    return `${symbol}${value.toLocaleString()}`;
  },

  // Percentage formatting
  percentage: (value: number, decimals: number = 1): string => {
    return `${value.toFixed(decimals)}%`;
  },

  // Years formatting
  years: (value: number): string => {
    return `${value} year${value !== 1 ? 's' : ''}`;
  },

  // Months formatting
  months: (value: number): string => {
    return `${value} month${value !== 1 ? 's' : ''}`;
  },

  // Compact number formatting (e.g., 1.2M, 450K)
  compactNumber: (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toString();
  },

  // Time-based formatting
  timeHorizon: (value: number): string => {
    if (value === 1) return '1 year';
    if (value < 12) return `${value} years`;
    if (value % 12 === 0) return `${value / 12} decade${value / 12 !== 1 ? 's' : ''}`;
    return `${value} years`;
  },

  // Rate formatting with context
  rate: (value: number, type: 'interest' | 'return' | 'appreciation' | 'inflation'): string => {
    const formatted = `${value.toFixed(2)}%`;
    switch (type) {
      case 'interest': return `${formatted} APR`;
      case 'return': return `${formatted} annual return`;
      case 'appreciation': return `${formatted} per year`;
      case 'inflation': return `${formatted} inflation`;
      default: return formatted;
    }
  }
};

// ===== VALIDATION UTILITIES =====

export const validators = {
  // Common range validators
  isPositive: (value: number): boolean => value > 0,
  isNonNegative: (value: number): boolean => value >= 0,
  isPercentage: (value: number): boolean => value >= 0 && value <= 100,
  isReasonableRate: (value: number): boolean => value >= 0 && value <= 30,
  isReasonablePrice: (value: number): boolean => value >= 1000 && value <= 10000000,

  // Date and time validators
  isReasonableYears: (value: number): boolean => value >= 1 && value <= 50,
  isAdultAge: (value: number): boolean => value >= 18 && value <= 120,
  isRetirementAge: (value: number): boolean => value >= 50 && value <= 90,

  // Financial validators
  isReasonableIncome: (value: number): boolean => value >= 10000 && value <= 2000000,
  isReasonableSavings: (value: number): boolean => value >= 0 && value <= 10000000,
  
  // Custom validation with context
  validateDownPayment: (amount: number, housePrice: number): { valid: boolean; warning?: string } => {
    const percentage = (amount / housePrice) * 100;
    if (percentage < 5) {
      return { valid: false };
    }
    if (percentage < 10) {
      return { valid: true, warning: 'Down payment below 10% may require mortgage insurance' };
    }
    if (percentage > 50) {
      return { valid: true, warning: 'Very high down payment - consider opportunity cost' };
    }
    return { valid: true };
  },

  validateInterestRate: (rate: number, type: 'mortgage' | 'investment' | 'loan'): { valid: boolean; warning?: string } => {
    const ranges = {
      mortgage: { min: 1, max: 15, typical: [3, 8] },
      investment: { min: 0, max: 20, typical: [4, 12] },
      loan: { min: 1, max: 25, typical: [5, 18] }
    };

    const range = ranges[type];
    if (rate < range.min || rate > range.max) {
      return { valid: false };
    }
    if (rate < range.typical[0] || rate > range.typical[1]) {
      return { valid: true, warning: `This ${type} rate seems unusual - verify current market rates` };
    }
    return { valid: true };
  }
};

// ===== CALCULATION UTILITIES =====

export const calculations = {
  // Monthly payment calculation
  calculateMonthlyPayment: (principal: number, annualRate: number, years: number): number => {
    const monthlyRate = annualRate / 100 / 12;
    const numPayments = years * 12;
    if (monthlyRate === 0) return principal / numPayments;
    
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
           (Math.pow(1 + monthlyRate, numPayments) - 1);
  },

  // Future value calculation
  calculateFutureValue: (presentValue: number, rate: number, years: number): number => {
    return presentValue * Math.pow(1 + rate / 100, years);
  },

  // Present value calculation
  calculatePresentValue: (futureValue: number, rate: number, years: number): number => {
    return futureValue / Math.pow(1 + rate / 100, years);
  },

  // Compound annual growth rate
  calculateCAGR: (startValue: number, endValue: number, years: number): number => {
    return (Math.pow(endValue / startValue, 1 / years) - 1) * 100;
  },

  // Tax calculations
  calculateAfterTax: (amount: number, taxRate: number): number => {
    return amount * (1 - taxRate / 100);
  },

  // Inflation adjustment
  adjustForInflation: (amount: number, inflationRate: number, years: number): number => {
    return amount / Math.pow(1 + inflationRate / 100, years);
  }
};

// ===== MODAL HANDLING UTILITIES =====

export class ModalManager {
  private static instances: Map<string, ModalManager> = new Map();
  private modalState: Map<string, boolean> = new Map();
  private callbacks: Map<string, (() => void)[]> = new Map();

  static getInstance(componentId: string): ModalManager {
    if (!this.instances.has(componentId)) {
      this.instances.set(componentId, new ModalManager());
    }
    return this.instances.get(componentId)!;
  }

  openModal(modalId: string, callback?: () => void): void {
    this.modalState.set(modalId, true);
    if (callback) {
      if (!this.callbacks.has(modalId)) {
        this.callbacks.set(modalId, []);
      }
      this.callbacks.get(modalId)!.push(callback);
    }
  }

  closeModal(modalId: string): void {
    this.modalState.set(modalId, false);
    // Execute callbacks
    const callbacks = this.callbacks.get(modalId);
    if (callbacks) {
      callbacks.forEach(cb => cb());
      this.callbacks.delete(modalId);
    }
  }

  isModalOpen(modalId: string): boolean {
    return this.modalState.get(modalId) || false;
  }

  toggleModal(modalId: string): void {
    if (this.isModalOpen(modalId)) {
      this.closeModal(modalId);
    } else {
      this.openModal(modalId);
    }
  }

  closeAllModals(): void {
    for (const modalId of this.modalState.keys()) {
      this.closeModal(modalId);
    }
  }
}

// ===== INPUT HANDLING UTILITIES =====

export const inputHandlers = {
  // Safe number parsing
  parseNumber: (value: string | number, fallback: number = 0): number => {
    if (typeof value === 'number') return value;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  },

  // Safe integer parsing
  parseInt: (value: string | number, fallback: number = 0): number => {
    if (typeof value === 'number') return Math.round(value);
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  },

  // Clamp value between min and max
  clamp: (value: number, min: number, max: number): number => {
    return Math.min(Math.max(value, min), max);
  },

  // Debounced input handler
  createDebouncedHandler: (handler: (value: any) => void, delay: number = 300) => {
    let timeoutId: NodeJS.Timeout;
    return (value: any) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => handler(value), delay);
    };
  },

  // Percentage to decimal conversion
  percentageToDecimal: (percentage: number): number => percentage / 100,
  decimalToPercentage: (decimal: number): number => decimal * 100,

  // Currency string to number
  currencyToNumber: (currencyString: string): number => {
    return parseFloat(currencyString.replace(/[^0-9.-]/g, '')) || 0;
  }
};

// ===== DATA PERSISTENCE UTILITIES =====

export const storage = {
  // Local storage with type safety
  save: <T>(key: string, data: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.log(`Failed to save to localStorage: ${error}`);
    }
  },

  load: <T>(key: string, fallback: T): T => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : fallback;
    } catch (error) {
      console.log(`Failed to load from localStorage: ${error}`);
      return fallback;
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.log(`Failed to remove from localStorage: ${error}`);
    }
  },

  // Session storage
  saveSession: <T>(key: string, data: T): void => {
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.log(`Failed to save to sessionStorage: ${error}`);
    }
  },

  loadSession: <T>(key: string, fallback: T): T => {
    try {
      const item = sessionStorage.getItem(key);
      return item ? JSON.parse(item) : fallback;
    } catch (error) {
      console.log(`Failed to load from sessionStorage: ${error}`);
      return fallback;
    }
  }
};

// ===== ERROR HANDLING UTILITIES =====

export const errorHandlers = {
  // Graceful error handling with user-friendly messages
  handleCalculationError: (error: Error, context: string): string => {
    console.error(`Calculation error in ${context}:`, error);
    return `Unable to calculate ${context}. Please check your inputs and try again.`;
  },

  handleValidationError: (field: string, value: any): string => {
    return `Invalid value for ${field}: ${value}. Please enter a valid number.`;
  },

  handleApiError: (error: any, action: string): string => {
    console.error(`API error during ${action}:`, error);
    if (error?.response?.status === 429) {
      return 'Too many requests. Please wait a moment and try again.';
    }
    if (error?.response?.status >= 500) {
      return 'Server error. Please try again later.';
    }
    return `Unable to ${action}. Please check your connection and try again.`;
  }
};

// ===== COMMON CONSTANTS =====

export const constants = {
  // Canadian financial constants
  MAX_RRSP_CONTRIBUTION: 30780,
  MAX_TFSA_CONTRIBUTION: 7000,
  CPP_MAX_MONTHLY: 1306.57,
  OAS_MAX_MONTHLY: 691.00,

  // US financial constants
  MAX_401K_CONTRIBUTION: 23000,
  MAX_IRA_CONTRIBUTION: 7000,
  SOCIAL_SECURITY_MAX_MONTHLY: 4555,

  // Common rates and limits
  TYPICAL_INFLATION_RATE: 2.5,
  TYPICAL_INVESTMENT_RETURN: 7.0,
  TYPICAL_MORTGAGE_RATE: 5.5,
  
  // Validation ranges
  MIN_AGE: 18,
  MAX_AGE: 120,
  MIN_RETIREMENT_AGE: 50,
  MAX_RETIREMENT_AGE: 90,
  MIN_HOUSE_PRICE: 50000,
  MAX_HOUSE_PRICE: 10000000,
  MIN_DOWN_PAYMENT_PERCENT: 5,
  MAX_DOWN_PAYMENT_PERCENT: 50
};

export default {
  formatters,
  validators,
  calculations,
  ModalManager,
  inputHandlers,
  storage,
  errorHandlers,
  constants
};